import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type DetalheCard } from "../api";
import { IconClose, IconSearch } from "../icons";
import { ICONS, type IconName } from "../icons";

/**
 * O QUE ESTÁ POR TRÁS DO NÚMERO — dois cliques num card do painel.
 *
 * O painel dizia "2 em análise" e parava aí: para saber QUAIS eram as duas, a
 * pessoa ia até a aba e caçava na lista. Aqui a lista já vem pronta, e cada
 * linha traz a ETAPA — quanto já foi preenchido, se está esperando conferência,
 * quantos frascos faltam bipar, há quantos dias está atrasada.
 *
 * A tabela é genérica: as colunas vêm do servidor (server/dashboard-cards.ts),
 * então card novo não precisa de código novo aqui.
 */
export default function CardDetalhe({
  card,
  titulo,
  icone,
  onCancel,
}: {
  card: string;
  /** O rótulo do card clicado — some do título só quando o servidor manda o seu. */
  titulo: string;
  icone?: IconName;
  onCancel: () => void;
}) {
  const [dados, setDados] = useState<DetalheCard | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(null);
    api.dashboardCard
      .detalhar(card)
      .then((d) => vivo && setDados(d))
      .catch((e) => vivo && setErro(e instanceof ApiError ? e.message : "Erro ao abrir a lista."))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [card]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const itens = dados?.itens ?? [];
    if (!q) return itens;
    return itens.filter((linha) =>
      Object.entries(linha)
        .filter(([k]) => k !== "tom" && k !== "id")
        .some(([, v]) => String(v ?? "").toLowerCase().includes(q)),
    );
  }, [dados, busca]);

  const Icone = icone ? ICONS[icone] : null;

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card modal-wide card-detalhe" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            {Icone && (
              <span className="rf-head-icon">
                <Icone size={20} />
              </span>
            )}
            <div>
              <h3>{dados?.titulo ?? titulo}</h3>
              <p className="rf-head-sub">
                {dados?.descricao ?? "Carregando os registros deste indicador…"}
              </p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancel} title="Fechar (Esc)">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body cd-corpo">
          {carregando && <div className="empty">Carregando…</div>}
          {erro && <div className="form-error">{erro}</div>}

          {dados && !carregando && (
            <>
              {dados.itens.length > 8 && (
                <div className="search-wrap cd-busca">
                  <IconSearch size={16} />
                  <input
                    className="search"
                    placeholder="Filtrar nesta lista…"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </div>
              )}

              {dados.itens.length === 0 ? (
                <div className="empty">
                  Nada aqui — este indicador está zerado agora. É o que o card mostra.
                </div>
              ) : filtrados.length === 0 ? (
                <div className="empty">Nenhuma linha para “{busca}”.</div>
              ) : (
                <div className="table-wrap">
                  <table className="table cd-tabela">
                    <thead>
                      <tr>
                        {dados.colunas.map((c) => (
                          <th key={c.chave}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtrados.map((linha, i) => (
                        <tr key={String(linha.id ?? i)} className={`cd-linha tom-${linha.tom ?? "neutro"}`}>
                          {dados.colunas.map((c) => (
                            <td key={c.chave} className={c.chave === "etapa" ? "cd-etapa" : undefined}>
                              {c.chave === "etapa" ? (
                                <span className="cd-etapa-txt">{String(linha[c.chave] ?? "—")}</span>
                              ) : (
                                String(linha[c.chave] ?? "") || "—"
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          <span className="rf-status">
            {dados ? `${filtrados.length} de ${dados.total} registro(s)` : ""}
          </span>
          <span className="rf-atalho">Esc fecha</span>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
